/**
 * Post-auth redirect targets.
 *
 * A join link sends visitors through /login and, for new accounts, /onboarding
 * before they reach the ranking they were actually invited to. Each hop carries
 * the destination in ?redirect=, so every hop has to both sanitise it and pass
 * it along — dropping it strands people on the hub with no idea what happened.
 */

/**
 * Keep only in-app destinations.
 *
 * The target arrives from a query string and is therefore attacker-controlled;
 * anything protocol-relative ("//evil.com") or absolute would turn our own
 * login into an open redirect.
 */
export function safeRedirect(target: string | null | undefined): string | null {
  if (!target || !target.startsWith('/') || target.startsWith('//')) return null;
  return target;
}

/** The sanitised ?redirect= of the current URL, defaulting to the hub. */
export function redirectTarget(search: string | URLSearchParams): string {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  return safeRedirect(params.get('redirect')) ?? '/';
}

export const AUTH_REDIRECT_KEY = 'bt-vision-auth-redirect';

/** Remembered across re-reads — see consumeStoredRedirect. */
let consumedRedirect: string | null | undefined;

/**
 * The destination stashed before a magic-link round trip, read once.
 *
 * Reading clears the key, so the answer has to be memoised for the life of the
 * page: StrictMode double-invokes effects, and sign-in re-renders the guards
 * mid-flight. Every repeat read would otherwise come back empty and silently
 * demote a deep link to the hub.
 */
export function consumeStoredRedirect(): string | null {
  if (consumedRedirect === undefined) {
    consumedRedirect = safeRedirect(localStorage.getItem(AUTH_REDIRECT_KEY));
    localStorage.removeItem(AUTH_REDIRECT_KEY);
  }
  return consumedRedirect;
}
